import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/doctocat/doctocat/theme/src/components/layout.js";
import { Box } from '@primer/react';
import { StatusLabel } from '@primer/gatsby-theme-doctocat';
import StorybookLink from '@primer/gatsby-theme-doctocat/src/components/storybook-link';
import LookbookLink from '@primer/gatsby-theme-doctocat/src/components/lookbook-link';
import SourceLink from '@primer/gatsby-theme-doctocat/src/components/source-link';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`When you create a Markdown file (`}<inlineCode parentName="p">{`.md`}</inlineCode>{` or `}<inlineCode parentName="p">{`.mdx`}</inlineCode>{`) in `}<inlineCode parentName="p">{`src/pages/`}</inlineCode>{`, you can define variables to provide additional information about that page. This information is called front matter and is set between triple dashes starting on the first line of the file:`}</p>
    <pre><code parentName="pre" {...{}}>{`---
title: Example title
---
`}</code></pre>
    <p>{`Doctocat recognizes some specific front matter variables described below.`}</p>
    <h2>{`Title and description`}</h2>
    <p><inlineCode parentName="p">{`title`}</inlineCode>{` is used to render the page heading and generate the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/title"
      }}>{`page title`}</a>{`. `}<inlineCode parentName="p">{`description`}</inlineCode>{` is used to create a `}<a parentName="p" {...{
        "href": "https://moz.com/learn/seo/meta-description"
      }}>{`meta description tag`}</a>{` which is important for SEO. We recommend setting `}<inlineCode parentName="p">{`title`}</inlineCode>{` and `}<inlineCode parentName="p">{`description`}</inlineCode>{` for every page:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`---
title: My page title
description: My page description
---
`}</code></pre>
    <h2>{`Component metadata`}</h2>
    <p>{`To auto-populate the `}<inlineCode parentName="p">{`title`}</inlineCode>{` and `}<inlineCode parentName="p">{`description`}</inlineCode>{` of a component page using metadata from the `}<a parentName="p" {...{
        "href": "https://github.com/primer/component-metadat"
      }}>{`primer/component-metadata`}</a>{` repository, add the `}<inlineCode parentName="p">{`componentId`}</inlineCode>{` frontmatter variable:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`---
componentId: avatar
---
`}</code></pre>
    <Note mdxType="Note">
      <p><inlineCode parentName="p">{`componentId`}</inlineCode>{` must match the `}<inlineCode parentName="p">{`id`}</inlineCode>{` of a component in this `}<a parentName="p" {...{
          "href": "https://github.com/primer/component-metadata/blob/main/metadata.json"
        }}>{`metadata.json`}</a>{` file.`}</p>
    </Note>
    <h2>{`Status`}</h2>
    <p>{`You can add a status label to any page by setting a `}<inlineCode parentName="p">{`status`}</inlineCode>{` variable in the frontmatter:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`---
status: Stable
---
`}</code></pre>
    <p>{`Doctocat comes with a few predefined statuses that have colors associated with them:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Output`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Alpha`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><StatusLabel status="Alpha" mdxType="StatusLabel" /></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Experimental. Breaking changes are likely.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Beta`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><StatusLabel status="Beta" mdxType="StatusLabel" /></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Usage encouraged, but not required.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Stable`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><StatusLabel status="Stable" mdxType="StatusLabel" /></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Usage expected.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Deprecated`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><StatusLabel status="Deprecated" mdxType="StatusLabel" /></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Usage not allowed. May be removed at any time.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Custom statuses will render a gray dot:`}</p>
    <StatusLabel status="My custom status" mdxType="StatusLabel" />
    <h2>{`Source`}</h2>
    <p>{`Sometimes it's useful to provide quick access to relevant source code. You can use the `}<inlineCode parentName="p">{`source`}</inlineCode>{` front matter variable to do that:`}</p>
    <pre><code parentName="pre" {...{}}>{`---
source: https://github.com/path/to/code
---
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`source`}</inlineCode>{` front matter variable is defined, Doctocat will add a source code link to the top of the page, like this:`}</p>
    <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2} p={4} mb={3} mdxType="Box">
  <SourceLink href="#" mdxType="SourceLink" />
    </Box>
    <Note mdxType="Note">
      <p>{`This source code link is not the same as the "Edit this page on GitHub" link that is automatically added to the bottom of every page. The source code link should point to code that is relevant to the page. The "Edit this page on GitHub" link points to the Markdown file for the page itself.`}</p>
    </Note>
    <h2>{`Storybook`}</h2>
    <p>{` You can use the `}<inlineCode parentName="p">{`storybook`}</inlineCode>{` front matter variable to link to `}<a parentName="p" {...{
        "href": "https://storybook.js.org/"
      }}>{`Storybook`}</a>{` stories:`}</p>
    <pre><code parentName="pre" {...{}}>{`---
storybook: https://primer.style/view-components/stories/?path=/story/primer-avatar-component
---
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`storybook`}</inlineCode>{` front matter variable is defined, Doctocat will add a storybook link to the top of the page, like this:`}</p>
    <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2} p={4} mb={3} mdxType="Box">
  <StorybookLink href="#" mdxType="StorybookLink" />
    </Box>
    <h2>{`Lookbook`}</h2>
    <p>{` You can use the `}<inlineCode parentName="p">{`lookbook`}</inlineCode>{` front matter variable to link to `}<a parentName="p" {...{
        "href": "http://lookbook.build/"
      }}>{`Lookbook`}</a>{` previews:`}</p>
    <pre><code parentName="pre" {...{}}>{`---
lookbook: https://primer.style/view-components/lookbook/inspect/primer/avatar
---
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`lookbook`}</inlineCode>{` front matter variable is defined, Doctocat will add a lookbook link to the top of the page, like this:`}</p>
    <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2} p={4} mb={3} mdxType="Box">
  <LookbookLink href="#" mdxType="LookbookLink" />
    </Box>
    <h2>{`Additional contributors`}</h2>
    <p>{`If you have the `}<inlineCode parentName="p">{`repository`}</inlineCode>{` in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` and `}<inlineCode parentName="p">{`repoRootPath`}</inlineCode>{` in `}<inlineCode parentName="p">{`gatsby-config.js`}</inlineCode>{` setup correctly (as described in the `}<a parentName="p" {...{
        "href": "/usage/customization#contributors"
      }}>{`customization`}</a>{` guide), Doctocat will automatically display contributors on the bottom of every page. These contributors are determined by commits. However, we know that commits aren't the only way to contribute. To give credit to people who aren't listed in the commit history but still contributed, use the `}<inlineCode parentName="p">{`additionalContributors`}</inlineCode>{` front matter variable to list their GitHub usernames, for example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`---
additionalContributors:
  - ashygee
  - emplums
---
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      